/**
 * Injection key and no-op implementation for optional performance diagnostics.
 *
 * The no-op keeps application code independent from browser timing APIs when
 * profiling is disabled, including during tests and production builds.
 */
export const PERFORMANCE_TELEMETRY_KEY = Symbol('performance-telemetry');

export const createNoopPerformanceTelemetry = () => ({
  startRun: () => createNoopPerformanceTelemetry(),
  startSpan: () => null,
  endSpan: () => {},
  recordEvent: () => {},
  increment: () => {},
  reset: () => {}
});

/**
 * Keeps a stable collaborator while switching the active run between loads.
 *
 * Components can retain this proxy across retries; each method delegates to
 * the latest run scope selected by `startRun`.
 *
 * @param {Object} telemetry - Root performance telemetry collaborator.
 * @returns {Object} Run-aware telemetry proxy.
 */
export const createRunTelemetry = (telemetry) => {
  let activeTelemetry = telemetry;
  const spanOwners = new WeakMap();

  const runTelemetry = {
    startRun(name, metadata = {}) {
      activeTelemetry = telemetry.startRun(name, metadata);
      return runTelemetry;
    },
    startSpan: (...args) => {
      const owner = activeTelemetry;
      const span = owner.startSpan(...args);
      if (span && typeof span === 'object') spanOwners.set(span, owner);
      return span;
    },
    endSpan: (span, ...args) => {
      const owner =
        span && typeof span === 'object'
          ? spanOwners.get(span) || activeTelemetry
          : activeTelemetry;
      owner.endSpan(span, ...args);
      if (span && typeof span === 'object') spanOwners.delete(span);
    },
    recordEvent: (...args) => activeTelemetry.recordEvent(...args),
    increment: (...args) => activeTelemetry.increment(...args),
    reset: (...args) => activeTelemetry.reset(...args)
  };

  return runTelemetry;
};
